import { Button, TextInput } from 'lego-on-react';
import React, { useCallback, useState } from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/EditorFields';
import block from 'propmods';

import { updateMainpageSettings as updateHomepageSettings } from '../../api';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import { useFormSettings } from '../../hooks/useFormSettings';
import { getHomepageFields, HomepageBadgeType } from '../../model/skill';
import { getBaseTooltipReducer } from '../../reducers';
import { ActionButton } from '../ActionButton/ActionButton';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { Field } from '../Form/Form';
import { PageHeadline } from '../Typo/Typo';

import './EditorFieldsPage.scss';

const b = block('EditorFieldsPage');

const t = i18n(keyset);

export const EDITOR_FIELDS_BADGES = [
    {
        value: HomepageBadgeType.Kids,
        label: t('badge-kids'),
    },
    {
        value: HomepageBadgeType.New,
        label: t('badge-new'),
    },
    {
        value: HomepageBadgeType.Puzzle,
        label: t('badge-puzzle'),
    },
    {
        value: HomepageBadgeType.Quest,
        label: t('badge-quest'),
    },
    {
        value: HomepageBadgeType.AliceChoice,
        label: t('badge-alice-choice'),
    },
    {
        value: HomepageBadgeType.Helpful,
        label: t('badge-helpful'),
    },
    {
        value: HomepageBadgeType.Sport,
        label: t('badge-sport'),
    },
    {
        value: HomepageBadgeType.Interesting,
        label: t('badge-interesting'),
    },
    {
        value: HomepageBadgeType.Wonderful,
        label: t('badge-wonderful'),
    },
    {
        value: HomepageBadgeType.Tasty,
        label: t('badge-tasty'),
    },
] as const;

export const EDITOR_FIELDS_TAGS = [
    'kids',
    'game',
    'new',
    'smarthome',
    'quest',
    'internal',
    'external',
    'sport',
    'health',
    'puzzle',
    'education',
    'buy',
    'finance',
    'travel',
    'productivity',
    'help',
    'everyday',
    'auto',
    'video',
    'audio',
    'news',
    'culture',
] as const;

export function removeDuplicatesUnordered<T>(arr: T[]) {
    return Array.from(new Set(arr));
}

export const EditorFieldsPage: React.FC<{ disabled: boolean }> = ({ disabled }) => {
    const skill = useCurrentAliceApp()!;

    const { settings, onChange } = useFormSettings({ defaultSettings: getHomepageFields(skill) });
    const [customTag, setCustomTag] = useState('');
    const [selectableTags, setSelectableTags] = useState(
        removeDuplicatesUnordered([...settings.tags, ...EDITOR_FIELDS_TAGS]).filter(Boolean),
    );

    const onCustomTagAdd = useCallback(() => {
        setSelectableTags([customTag, ...selectableTags]);
        onChange('tags')([...settings.tags, customTag]);
        setCustomTag('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customTag, settings.tags, selectableTags]);

    const onSave = useCallback(async () => {
        await updateHomepageSettings(skill.id, settings, skill.channel);
    }, [skill.id, settings, skill.channel]);

    return (
        <ErrorBoundary withHomepageLink>
            <div {...b()}>
                <div {...b('fields')}>
                    <PageHeadline title={t('headline')} />
                    <Field label={t('title')}>
                        <TextInput
                            theme="normal"
                            size="s"
                            text={settings.editorName}
                            onChange={onChange('editorName')}
                            disabled={disabled}
                        />
                    </Field>
                    <Field label={t('description')}>
                        <TextInput
                            theme="normal"
                            size="s"
                            text={settings.editorDescription}
                            onChange={onChange('editorDescription')}
                            disabled={disabled}
                        />
                    </Field>
                    <Field label={t('badges')}>
                        <div {...b('radios')}>
                            {EDITOR_FIELDS_BADGES.map(({ label, value }) => {
                                const selectedBadges = settings.homepageBadgeTypes;
                                const checked = selectedBadges.includes(value);

                                return (
                                    <div key={value} {...b('button')}>
                                        <Button
                                            size="s"
                                            type="check"
                                            checked={checked}
                                            theme="normal"
                                            onClick={() => {
                                                checked
                                                    ? onChange('homepageBadgeTypes')(
                                                          selectedBadges.filter((x) => x !== value),
                                                      )
                                                    : onChange('homepageBadgeTypes')([...selectedBadges, value]);
                                            }}
                                            disabled={disabled}
                                        >
                                            {label}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </Field>
                    <Field label={t('tags')}>
                        <div {...b('tag-input-field')}>
                            <div {...b('tag-input-container')}>
                                <TextInput
                                    size="s"
                                    theme="normal"
                                    text={customTag}
                                    onChange={(value) => {
                                        setCustomTag(value.replace(/[^\w]/g, ''));
                                    }}
                                    disabled={disabled}
                                />
                            </div>
                            <Button
                                size="s"
                                theme="action"
                                onClick={onCustomTagAdd}
                                disabled={disabled || customTag.length === 0}
                            >
                                {t('add')}
                            </Button>
                        </div>
                        <div {...b('radios')}>
                            {selectableTags.map((tag) => {
                                const checked = settings.tags.includes(tag);

                                return (
                                    <div key={tag} {...b('button')}>
                                        <Button
                                            size="s"
                                            type="check"
                                            checked={checked}
                                            theme="normal"
                                            onClick={() => {
                                                checked
                                                    ? onChange('tags')(settings.tags.filter((x) => x !== tag))
                                                    : onChange('tags')([...settings.tags, tag]);
                                            }}
                                            disabled={disabled}
                                        >
                                            {tag}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </Field>
                </div>
                <div {...b('save-button')}>
                    <ActionButton
                        tooltipReducer={getBaseTooltipReducer({ successLabel: t('saved') })}
                        defaultTooltipState={{ tooltipVisible: false }}
                        action={onSave}
                        disabled={disabled}
                    >
                        {t('save')}
                    </ActionButton>
                </div>
            </div>
        </ErrorBoundary>
    );
};
